// frontend/src/utils/workforceTimeUtils.js
//
// Display helpers for workforce time-tracking screens (TIME-004a+).
//
// CLAUDE.md §"Design Tokens": "All times displayed in America/Phoenix timezone
// regardless of device locale. Always label times 'MST' in the UI." These
// helpers enforce that contract for the clock-in / clock-out / break flows.
//
// Kept separate from workforceUtils.js so the existing TIME-001 helpers
// (currency, employment-type lookups, etc.) do not need to import a
// timezone library.

import moment from "moment-timezone";

export const STUDIO_TZ = "America/Phoenix";
export const STUDIO_TZ_LABEL = "MST";

/**
 * Render an ISO/Date as "h:mm AM/PM MST" — the format every clock screen
 * status line uses (TIME-004a §"Body (clocked in)" + TIME-005 §"Region 1").
 * Returns "" for falsy / unparseable input.
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const formatStudioTime = (value) => {
  if (!value) return "";
  const m = moment.tz(value, STUDIO_TZ);
  if (!m.isValid()) return "";
  return `${m.format("h:mm A")} ${STUDIO_TZ_LABEL}`;
};

/**
 * Render an elapsed duration since the supplied start instant as "HHh MMm"
 * (e.g., "03h 18m"). Clamps to "00h 00m" when start is missing, in the
 * future, or unparseable. `now` is injectable for deterministic tests.
 * @param {string|Date|null|undefined} startInstant
 * @param {Date} [now]
 * @returns {string}
 */
export const formatElapsedSince = (startInstant, now) => {
  if (!startInstant) return "00h 00m";
  const startMs = new Date(startInstant).getTime();
  if (Number.isNaN(startMs)) return "00h 00m";
  const nowMs = (now instanceof Date ? now : new Date()).getTime();
  const diffMs = Math.max(0, nowMs - startMs);
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}h ${String(minutes).padStart(
    2,
    "0"
  )}m`;
};
